import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { intelligence } from '@cwos/api-client';
import { BrandMark, ConsequenceNote, Icon, Refused } from '@cwos/ui-admin';
import {
  answerLimits,
  groundingCaution,
  isConfirmationNotLive,
  isProviderUnconfigured,
  isTransmissionDenied,
  isWithheld,
  NO_PROSE_ANSWER,
  readableAnswer,
  turnEndingNote,
} from '../turnView.js';
import { evidenceLines } from '../evidenceView.js';
import { ResultBlocks, resultActionMessage, type ResultAction } from '../results/index.js';
import { ProposedActions } from '../actions/ProposedActions.js';
import { ExplanationPanel } from '../explain/ExplanationPanel.js';
import type { WireScreenContext } from './screenContext.js';

/**
 * THE question→answer surface over `POST /api/intelligence/turn`, wearing
 * whatever chrome its host gives it.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * F224 IS CLOSED: THERE IS NOW ONE CONVERSATION, NOT TWO.
 *
 * This file used to be a prop-driven COPY of the Ask Phibian page's thread and
 * dock. The page had its own `ExchangeCard`, `WorkingTurn`, `EvidencePanel`,
 * `ConfirmationCard` and `Composer`, all module-private, and the global panel
 * could not reuse them — so they were duplicated here, with a comment naming
 * the cost and deferring the fix.
 *
 * The fix is made. `Intelligence.tsx` no longer renders a thread or a dock of
 * its own: it renders THIS component and supplies the page's chrome (header,
 * history sidebar, empty state) around it. Both the page and the global panel
 * are now hosts of one implementation, which is why the honesty rules below
 * can be stated once instead of maintained in parallel.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT IT PROMISES.
 *
 *   - Every answer renders its provenance and its grounding verdict. A verdict
 *     other than `grounded` is a visible caution, and the answer text is never
 *     rewritten or trimmed — hiding the model's words would hide exactly what
 *     the audit exists to expose.
 *   - A proposed change renders as a confirmation card and runs ONLY on the
 *     person's explicit confirm, which re-posts the server-issued
 *     `proposalId` + `planDigest` pair. Dismiss is local; an unconsumed
 *     confirmation expires server-side.
 *   - Only the LATEST answer's proposal is offered. Two Confirm buttons for
 *     two different diffs is how the wrong one gets clicked.
 *   - A RESULT CARD'S BUTTON IS A TURN, NOT A SHORTCUT PAST ONE. [Save to
 *     Dashboards], [Edit] and [Review automation] send the sentence the person
 *     could have typed, down the same path a typed message takes. Both hosts
 *     get this from here rather than wiring it themselves — see `actOnResult`.
 *   - NO RUNTIME VOCABULARY REACHES A READER. No tool ids, no manifest
 *     ordinals (`@2`), no metric catalog keys, no content hashes, no
 *     "retrievals", no "evidence classes", no plan ids. Not in a disclosure,
 *     not behind a toggle, not in a `data-` attribute. See the note on the
 *     developer disclosure below.
 *   - A FINISHED ANSWER SHOWS THE RESULT, NOT A TRACE. See `WorkingTurn`.
 *     Unchanged by F221b's progress channel, and worth stating precisely
 *     because the two are easy to confuse: a RUNNING turn may report the kind
 *     of work it is doing, and a FINISHED one still narrates nothing. The
 *     phase line lives only in `WorkingTurn`, which is unmounted the instant
 *     the answer lands and leaves nothing behind it.
 *   - An unconfigured provider is an honest state with no retry affordance.
 *     A 403 is TWO states, not one: `intelligence:use` withheld from this
 *     account (a grant an org admin can make) and
 *     `intelligence_transmission_denied` (a deployment data-classification
 *     decision no grant can lift). They are rendered separately because their
 *     remedies are different people.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE DEVELOPER DISCLOSURE IS GONE, NOT HIDDEN.
 *
 * There used to be a nested "Developer detail" under the evidence panel and
 * under the capability list, printing `analytics.query_metric@2 · 412
 * time_sessions · hash 1a2b…`. It has been deleted outright rather than moved
 * behind a developer-mode flag, and the reason is that a flag would not have
 * fixed the actual problem. A gated disclosure is still code that renders wire
 * identifiers into this DOM, so the strongest property the suite could assert
 * would be "not by default" — one regression, one default flip, one shared
 * screenshot away from being false. Deleted, the property is absolute: the
 * strings are not in the component at any setting.
 *
 * Nothing was lost that a developer needs. The turn response carries
 * `evidence`, `grounding` and `summary` in full; the network tab shows it
 * verbatim, and the server records the same audit on its own side. The refs a
 * grounding verdict names are still on the wire — they are simply not spoken
 * to a person who did not ask for a manifest.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `wireContext`, the one thing this adds over a bare thread.
 *
 * When present it rides EVERY turn this surface sends, including the
 * confirm-only one, as the request's `context` field. When absent the key is
 * omitted from the body entirely — not sent as `undefined`, not sent as `{}`.
 * The value is built by `screenContext.ts` from a closed typed union and is
 * ids and kinds only; this component never constructs one, never reads the
 * DOM, and never merges anything into what it is handed.
 */

/** One completed question → answer round, held locally for rendering only. */
interface Exchange {
  key: string;
  question: string;
  response: intelligence.TurnResponse;
}

/** What is on the wire right now, so the thread can show it honestly. */
type InFlight = { kind: 'ask'; question: string } | { kind: 'confirm' };

export interface ConversationSurfaceProps {
  /**
   * The typed screen context to carry on every turn, or `undefined` to send no
   * `context` key at all. Built by `toWireContext`; never assembled here.
   */
  readonly wireContext?: WireScreenContext | undefined;
  /**
   * What fills the thread before the first message. A render prop because the
   * empty state's starters have to be able to ASK, and `ask` is this
   * component's state machine, not its host's.
   */
  readonly renderEmpty?: (ask: (question: string) => void, disabled: boolean) => ReactNode;
  /**
   * Distinct id for the composer's label/textarea pair. Two conversation
   * surfaces can be mounted at once (the page, and the panel over it), and two
   * elements sharing one `id` is a broken label, not a cosmetic clash.
   */
  readonly composerId?: string;
  /** Focused when the host opens the surface (the panel does; the page does not). */
  readonly composerRef?: React.RefObject<HTMLTextAreaElement | null>;
  /**
   * Called after every answer lands, with the server's response.
   *
   * The page uses it to re-read the conversation list, because the FIRST
   * answer in a conversation is what creates the thread the sidebar will show.
   * It is a notification, not a hand-off: this component keeps owning the
   * exchange either way, so a host that ignores it still works.
   */
  readonly onAnswered?: (response: intelligence.TurnResponse) => void;
  /**
   * The DURABLE conversation this surface appends to, when the host opened an
   * existing one from history.
   *
   * Read ONCE, as this component's initial thread, for the same reason the
   * exchanges are local: a host that changed it mid-thread would silently move
   * a live conversation into a different history. Hosts switch conversations by
   * remounting (a React `key`), which is also what clears the previous
   * thread's exchanges.
   *
   * NOT a session id. `sessionId` keys the in-memory transcript the MODEL
   * sees and is the server's answer on every response; this keys the durable
   * record of what the PERSON saw. Absent changes nothing: the turn runs and
   * answers exactly as it did before durable threads existed, and nothing is
   * written.
   */
  readonly threadId?: string | undefined;
  /**
   * Starts a durable conversation for the FIRST message of a new one, and
   * resolves the thread id to append it to.
   *
   * Resolving `undefined`, or rejecting, means the turn runs UNSAVED — see
   * `resolveThreadId`. It is a hook rather than a thread id because the thread
   * cannot exist before the first message: the server derives the title from
   * it, and this product has no placeholder title.
   */
  readonly ensureThread?: ((firstMessage: string) => Promise<string | undefined>) | undefined;
  /**
   * The stored transcript of a reopened conversation, oldest first.
   *
   * Rendered above the live exchanges as a record of what was said, and
   * deliberately not as exchanges: a replayed answer has no live result cards
   * and no confirmation, because those were drawn from a plan that is no
   * longer live and a button over one would be an offer this surface cannot
   * honour.
   */
  readonly transcript?: readonly PriorMessage[] | undefined;
  /**
   * Text the composer opens with — "Explain Service hours delivered for Last
   * week" when a chart opened Ask. Read ONCE, like `threadId`; it is a draft
   * the person still sends, never a turn sent for them.
   */
  readonly initialDraft?: string | undefined;
}

/** One half of a replayed exchange. Text only — see `history/threads.ts`. */
export interface PriorMessage {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly body: string;
}

export function ConversationSurface({
  wireContext,
  renderEmpty,
  composerId = 'intelligence-question',
  composerRef,
  onAnswered,
  threadId,
  ensureThread,
  transcript,
  initialDraft,
}: ConversationSurfaceProps) {
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);

  /**
   * The durable thread this conversation is being written to, or undefined.
   *
   * A REF, not state, and read inside `mutationFn`: it is set at most once per
   * conversation and the value the running turn needs is the current one, not
   * the one the last render saw. A state value here would make the second
   * message of a brand-new conversation create a SECOND thread, because the
   * render carrying the first thread's id has not necessarily happened yet.
   */
  const threadRef = useRef<string | undefined>(threadId);
  /**
   * True once a thread could not be created for this conversation.
   *
   * Its own flag rather than an error: the turn ran, the answer is on screen
   * and its side effects are durable. Only the transcript is missing, and
   * saying so is the difference between a quiet gap and somebody believing a
   * conversation was recorded that was not.
   */
  const [notPersisting, setNotPersisting] = useState(false);
  const [draft, setDraft] = useState(initialDraft ?? '');
  const [inFlight, setInFlight] = useState<InFlight | null>(null);
  /** proposalId the person dismissed — local only; the row just expires. */
  const [dismissedProposal, setDismissedProposal] = useState<string | null>(null);

  /**
   * A SYNCHRONOUS latch against a double submit.
   *
   * `send.isPending` is a render-time value: the button the person clicked was
   * drawn by a render in which it was `false`, and React batches, so two
   * activations inside one frame — a double click, a held Enter, an assistive
   * device replaying an activation — BOTH read `false` and both fire a turn.
   * Withholding the buttons on the next render cannot help a second click that
   * has already happened. A ref flips the instant the first activation is
   * handled, before any re-render, so the second one has something true to
   * read. Cleared in `onSettled`, which runs whether the turn succeeded or
   * failed.
   */
  const turnInFlight = useRef(false);

  /**
   * The phase the running turn last reported, or null if it has not reported
   * one yet (which is every turn's first seconds, and every turn that calls no
   * tool at all).
   *
   * THIS IS A REPORT, NOT A GUESS. It is set only from a server frame that
   * says a tool round FINISHED — see `turnPhaseSchema` in the api-client and
   * `progress.ts` on the server. Nothing here infers a phase from elapsed
   * time, from the question's wording, or from what a turn "usually" does.
   * That distinction is the whole reason WorkingTurn was allowed to grow a
   * per-step line: its own docblock said the phrases become honest when a
   * channel exists that reports tool calls in flight, and until this lane
   * there was none.
   */
  const [phase, setPhase] = useState<intelligence.TurnPhase | null>(null);

  /**
   * The thread id to ride this turn, or undefined for an unsaved one.
   *
   * A CREATE FAILURE MUST NOT FAIL THE TURN. The question is answerable
   * whether or not it is recorded, and rejecting here would turn "we could not
   * save this" into "we could not answer this" — the strictly worse of the two
   * answers, and the one that invites a retry that runs the turn twice. So the
   * failure is swallowed into a flag the surface renders, and the turn goes
   * out without a `threadId`, which the route treats exactly as it treated
   * every turn before durable threads existed.
   *
   * A confirm-only request carries no message and therefore cannot start a
   * conversation — there is no first message for the server to title it from.
   * It appends to a thread that already exists, or to nothing.
   */
  const resolveThreadId = async (req: intelligence.TurnRequest): Promise<string | undefined> => {
    if (threadRef.current !== undefined) return threadRef.current;
    if (ensureThread === undefined || req.message === undefined) return undefined;
    try {
      const created = await ensureThread(req.message);
      if (created === undefined) {
        setNotPersisting(true);
        return undefined;
      }
      threadRef.current = created;
      return created;
    } catch {
      setNotPersisting(true);
      return undefined;
    }
  };

  const send = useMutation({
    mutationFn: async (req: intelligence.TurnRequest) => {
      const durable = await resolveThreadId(req);
      return intelligence.turn(durable === undefined ? req : { ...req, threadId: durable }, {
        onProgress: (frame) => {
          // `accepted` is deliberately not rendered. It carries only the
          // sessionId and the fact that the server has the turn — which is
          // already what the working state's existence says. Rendering it
          // would add a state change that means nothing new.
          if (frame.kind === 'progress') setPhase(frame.phase);
        },
      });
    },
    onSuccess: (response, req) => {
      setExchanges((prev) => [
        ...prev,
        {
          key: response.turnId,
          question: req.message ?? 'Confirmed the proposed action.',
          response,
        },
      ]);
      setSessionId(response.sessionId);
      setDismissedProposal(null);
      onAnswered?.(response);
    },
    onSettled: () => {
      turnInFlight.current = false;
      setInFlight(null);
      // The phase belongs to the turn that reported it. A finished answer shows
      // the result, not a trace, so it is dropped the moment the turn ends and
      // nothing replaces it — and dropping it here rather than at the next
      // turn's start also stops a stale phrase reappearing under a new
      // question for the moment before its first tool returns.
      setPhase(null);
    },
  });

  /**
   * The `context` key, or nothing.
   *
   * Spread at the call sites rather than assigned, so an absent context leaves
   * NO key on the body. `{ context: undefined }` would serialize away over
   * JSON, but it would also make the assertion "the body has no context" a
   * statement about a serializer instead of about this component.
   */
  const contextField = wireContext !== undefined ? { context: wireContext } : {};

  /**
   * THE path a message takes to the server.
   *
   * The composer, the empty state's starters and a result card's action button
   * all arrive here, and nothing downstream can tell which one it was: one
   * request shape, one session id, one screen context, one in-flight state, one
   * appended exchange. That is the whole reason the buttons route through this
   * function instead of owning a mutation of their own — whether a dashboard
   * gets saved must not depend on whether the person typed the sentence or
   * pressed the button that says it.
   *
   * `onSent` runs only once the answer lands, so a turn that failed leaves
   * whatever the caller was holding on to intact.
   */
  const sendMessage = (message: string, onSent?: () => void) => {
    const trimmed = message.trim();
    // Two guards, deliberately. `send.isPending` is the honest render-time
    // state; `turnInFlight` is the one that survives a second activation in the
    // same frame, which is the only way an action button can be pressed twice
    // at all (it is withheld from the next render — see the thread below).
    if (trimmed === '' || send.isPending || turnInFlight.current) return;
    turnInFlight.current = true;
    setInFlight({ kind: 'ask', question: trimmed });
    send.mutate(
      {
        message: trimmed,
        ...(sessionId !== undefined ? { sessionId } : {}),
        ...contextField,
      },
      { onSuccess: () => onSent?.() },
    );
  };

  /** The composer's send: the box empties when the answer lands, not before. */
  const ask = (message: string) => sendMessage(message, () => setDraft(''));

  /**
   * A result card's action button, as an ordinary turn.
   *
   * `resultActionMessage` is the sentence the person could have typed
   * themselves, so this is a shortcut THROUGH the conversation and not a back
   * channel around it: same route, same re-plan, same authorization, same
   * confirmation ceremony, same line in the thread, same session context. The
   * server cannot distinguish this from typing, which is the point — F258 was
   * that saving a dashboard existed only as a phrasing the model had to
   * recognise, and a phrasing is a probability. A button is not.
   *
   * Pressing [Save to Dashboards] still does not save a dashboard. It asks,
   * exactly as typing "save this" asks, and the confirmation card that comes
   * back is the only thing that can approve the change.
   *
   * The draft is deliberately NOT cleared here: the button sent its own
   * sentence, and a half-typed question in the composer belongs to the person
   * rather than to the card they clicked.
   *
   * An action kind this build does not know produces an empty sentence, which
   * `sendMessage` drops — better to do nothing visible than to send the server
   * a turn whose meaning this bundle had to guess.
   */
  const actOnResult = (action: ResultAction) => sendMessage(resultActionMessage(action));

  // The pending confirmation, if the LATEST answer proposed one and the person
  // has not dismissed it.
  const last = exchanges.length > 0 ? exchanges[exchanges.length - 1] : undefined;
  const pending =
    last?.response.pendingConfirmation !== undefined &&
    last.response.pendingConfirmation.proposalId !== dismissedProposal
      ? last.response.pendingConfirmation
      : undefined;

  const confirmPending = () => {
    if (pending === undefined || send.isPending || turnInFlight.current) return;
    turnInFlight.current = true;
    // Confirm-only (F187): no message rides the request — the server supplies
    // its canonical instruction. The screen context still does: the person is
    // confirming from the same screen they asked from.
    setInFlight({ kind: 'confirm' });
    send.mutate({
      ...(sessionId !== undefined ? { sessionId } : {}),
      ...contextField,
      confirmation: { proposalId: pending.proposalId, planDigest: pending.planDigest },
    });
  };

  const unconfigured = isProviderUnconfigured(send.error);
  // Checked before `withheld`, and `isWithheld` excludes it, so the two are
  // mutually exclusive by construction rather than by the order of two `if`s
  // that a later edit could reorder.
  const transmissionDenied = isTransmissionDenied(send.error);
  const withheld = isWithheld(send.error);
  const notLive = isConfirmationNotLive(send.error);

  // Guarded rather than called outright: jsdom has no layout and therefore no
  // `scrollIntoView`, and a screen that throws in a test environment is a
  // screen nobody can test.
  const tail = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = tail.current;
    if (node !== null && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'end' });
    }
  }, [exchanges.length, inFlight]);

  const replayed = transcript ?? [];
  const started = exchanges.length > 0 || inFlight !== null || replayed.length > 0;

  return (
    <>
      <div className="ap-thread">
        {started || renderEmpty === undefined ? null : renderEmpty(ask, send.isPending)}

        {replayed.length > 0 ? (
          <section className="ap-replay" aria-label="Earlier in this conversation">
            <p className="rn-muted ap-replay-head">
              Earlier in this conversation. This is the record of what was said — the evidence and
              the cards that were shown with it aren’t redrawn here.
            </p>
            <ol className="ap-turns">
              {replayed.map((message) => (
                <li key={message.id} className="ap-turn">
                  <div
                    className={
                      message.role === 'user' ? 'ap-said ap-said-you' : 'ap-said ap-said-phibian'
                    }
                  >
                    <p className="ap-role">
                      {message.role === 'user' ? (
                        'You'
                      ) : (
                        <>
                          <BrandMark size={16} />
                          Phibian
                        </>
                      )}
                    </p>
                    <p className="ap-text">{message.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {exchanges.length > 0 ? (
          <ol className="ap-turns" aria-label="Conversation">
            {exchanges.map((exchange) => (
              <ExchangeCard
                key={exchange.key}
                exchange={exchange}
                // WITHHELD WHILE A TURN RUNS, rather than greyed out. The
                // buttons live on cards this surface does not own
                // (`results/`), and those take no `busy` prop — reaching in to
                // disable another lane's control would be this file deciding
                // how that card renders. Absence is the honest form of the
                // same statement: while a turn is in flight there is no second
                // turn to offer, so nothing offers one.
                //
                // NOT restricted to the latest exchange, though — unlike the
                // confirmation card below, which is offered for the last
                // answer only because two live Confirm buttons is how the
                // wrong diff gets applied.
                //
                // F264 — WHY AN OLDER PREVIEW'S BUTTON STAYS LIVE, STATED
                // CORRECTLY. This comment used to say that an action "carries
                // the server-issued identity of the exact spec it was drawn
                // from", so a stale click would fail its digest binding. That
                // is not the mechanism. `actOnResult` sends
                // `resultActionMessage(action)` and nothing else, and the save
                // turn re-plans from prose
                // (`intelligence-turn-results.test.tsx` asserts the whole
                // request body).
                //
                // F290 CLOSED THE SEAM THAT INVITED THE WRONG STORY. The
                // action USED to carry a `planId` and a `diffDigest` that
                // nothing read; they are gone. They could never have done the
                // job the comment credited them with anyway: `planId` is the
                // first 128 bits of `diffDigest` (`dashboards/digest.ts`
                // `planIdFromDigest`), so it names a SPEC, and a matching one
                // would prove only that the re-plan reproduced the same spec —
                // never that a preview's identity travelled.
                //
                // WHAT ACTUALLY HOLDS. Two things, and neither is a binding on
                // the button:
                //   1. The sentence names the dashboard. An older preview's
                //      [Save to Dashboards] asks to save THAT dashboard by
                //      name, in the same words the person could have typed.
                //   2. Nothing is saved without a confirmation the person
                //      reads. The save turn issues one whose `summary`
                //      describes what the SERVER planned; a re-plan that
                //      produced the wrong dashboard shows up there, before
                //      anything is written, and can simply be declined.
                // The digest binding is real but it sits elsewhere:
                // `dashboard.create` refuses to execute unless the confirmation
                // it was handed names the plan it just re-planned
                // (`tools/dashboardTools.ts` — proposalId/planDigest vs
                // planId/diffDigest). That stops the plan drifting between the
                // confirmation card and execution. It cannot tell that a button
                // came from an older preview, because by then the older
                // preview's identity was never in the request.
                //
                // WHAT IS OBSERVED, NOT GUARANTEED. On staging, across four
                // usable observations, the confirmation named the plan the
                // button was drawn from every time — including once with a
                // different dashboard previewed in between. That is the
                // re-plan behaving well, not a property the code enforces: two
                // previews with the SAME name are indistinguishable in the
                // sentence, and the re-plan is steered by conversation context
                // this surface does not control. Leaving the older button live
                // is therefore a judgement that a wrong re-plan is VISIBLE and
                // DECLINABLE, not that it is impossible — and F290 is the
                // observation that those four samples came out right while the
                // identity fields sat unread, so they were never what made it
                // come out right.
                onAction={send.isPending ? undefined : actOnResult}
              />
            ))}
          </ol>
        ) : null}

        {inFlight !== null ? <WorkingTurn inFlight={inFlight} phase={phase} /> : null}

        {/* NOT an error, and not offered a retry. The turns ran and their
            answers are above; only the transcript is missing, and asking again
            would run every side effect a second time to fix a record. */}
        {notPersisting ? (
          <p className="rn-muted ap-notsaved" role="status">
            This conversation isn’t being saved. You can keep asking — it just won’t be in your
            history when you come back.
          </p>
        ) : null}

        {unconfigured ? <NotConfigured /> : null}
        {transmissionDenied ? (
          <Refused
            heading="Phibian isn’t cleared to read this organization’s records"
            message="This deployment’s data-classification policy does not allow your organization’s records to be sent to a language model, so the question was refused before anything left the server. This is not about your account and no permission grant will lift it — it is a deployment-level decision, and only whoever set that policy can change it."
          />
        ) : null}
        {withheld ? (
          <Refused
            heading="Intelligence isn’t open to you"
            message="Your account does not hold intelligence:use, so the assistant will not answer for it. It is withheld, not broken — ask your org admin if you need it; access is granted deliberately, never by default."
          />
        ) : null}

        <div ref={tail} aria-hidden="true" />
      </div>

      {unconfigured || withheld || transmissionDenied ? null : (
        <div className="ap-dock">
          {pending !== undefined ? (
            <ConfirmationCard
              pending={pending}
              busy={send.isPending}
              onConfirm={confirmPending}
              onDismiss={() => setDismissedProposal(pending.proposalId)}
            />
          ) : null}
          <Composer
            id={composerId}
            textareaRef={composerRef}
            draft={draft}
            onDraftChange={setDraft}
            onSubmit={() => ask(draft)}
            pending={send.isPending}
            error={send.isError ? send.error : null}
            confirmationNotLive={notLive}
          />
        </div>
      )}
    </>
  );
}

/**
 * One question → answer round.
 *
 * THE ORDER IS THE POINT: the answer first, then what it could not reach, then
 * its caution, then — collapsed — where it came from. A reader gets the result
 * in the first line and can stop there; everything below is available and
 * nothing below is in the way. The previous order opened with a step-by-step
 * trail of the tool loop, so the first thing a person read after asking "how
 * many hours did we deliver" was "Listed what can be measured".
 */
function ExchangeCard({
  exchange,
  onAction,
}: {
  exchange: Exchange;
  /** Turns a result card's button into the next turn. Absent while one runs. */
  onAction?: ((action: ResultAction) => void) | undefined;
}) {
  const { question, response } = exchange;
  const caution = groundingCaution(response.grounding);
  const limits = answerLimits(response.summary);
  const ending = turnEndingNote(response.summary);
  // The prose, with citation tokens cut. The server already cut them
  // (`redactCitations`); this is the render-side guarantee, because the leak
  // the owner photographed was a chat bubble and a bubble is the last place it
  // can be stopped. `null` means the turn produced no words at all — said
  // plainly rather than drawn as an empty bubble.
  const prose = readableAnswer(response.answer);
  /**
   * `threadAppended` is present ONLY when the request named a thread, and
   * `false` IS A REAL ANSWER rather than an error: the turn ran, its answer is
   * here, and its side effects — audit rows, a created dashboard, a consumed
   * confirmation — are already durable. Only the save to the transcript
   * failed. Rendering it as a failure would describe something that did not
   * happen and invite a retry that would run those side effects again; drop it
   * silently and somebody believes a conversation was recorded that was not.
   */
  const unsaved = response.threadAppended === false;
  return (
    <li className="ap-turn">
      <div className="ap-said ap-said-you">
        <p className="ap-role">You</p>
        <p className="ap-text">{question}</p>
      </div>
      <div className="ap-said ap-said-phibian">
        <p className="ap-role">
          <BrandMark size={16} />
          Phibian
        </p>
        <p className="ap-text">{prose ?? NO_PROSE_ANSWER}</p>
        {/* Typed operational objects (Lane C) render below the prose: the
            answer says it, the objects show it — and where a card offers to do
            something about it, the button sends the sentence the person would
            otherwise have had to type. */}
        <ResultBlocks results={response.results} onAction={onAction} />
        {unsaved ? (
          <p className="tc-note ap-caution" role="note">
            This answer wasn’t saved to the conversation, so it won’t be here when you come back. It
            still ran, and anything it changed still changed — don’t ask again to fix this.
          </p>
        ) : null}
        {limits !== null ? (
          <p className="tc-note ap-caution" role="note">
            {limits}
          </p>
        ) : null}
        {ending !== null ? (
          <p className="tc-note ap-caution" role="note">
            {ending}
          </p>
        ) : null}
        {caution !== null ? (
          // tone 'alert' is announced; tone 'note' (uncited, F186) is visible
          // but calm — a greeting is not an integrity event.
          <p
            className={caution.tone === 'alert' ? 'tc-warn ap-caution' : 'tc-note ap-caution'}
            role={caution.tone === 'alert' ? 'alert' : 'note'}
          >
            {caution.text}
          </p>
        ) : null}
        {/* B-ACTIONS. Below the answer and its cautions, above the evidence:
            what a person can DO about what they just read. The actions were
            derived server-side from this turn's sealed evidence manifest, not
            from the prose above them, and offering one is not authorizing it —
            the server re-authorizes when the button is pressed. */}
        <ProposedActions actions={response.actionProposals} withheld={response.withheldActions} />
        {/* B7a's §24 verdict, which this surface composed and then dropped on
            the floor until now. THREE ARMS AND THE PANEL KEEPS THEM APART:
            absent renders nothing (the ordinary case — a figure or a list
            carries no reasoning, and a warning on every one of those is how a
            signal becomes noise); `{ok:false}` renders an announced sentence
            saying a named check refused the model's reasoning, which is a
            different fact from having none; `{ok:true}` renders the reasoning
            with its own discriminants intact. It sits below the actions and
            above the evidence for the same reason the actions sit below the
            cautions: the result first, then what to do, then why, then where
            it came from. */}
        {/* THE MANIFEST GOES TO BOTH, and it is the same array. Every citation
            marker in the explanation is a POSITION in this list, and the panel
            below numbers the same list from the same object — so a "[2]" beside
            a sentence and the second line under "Based on Phibian records" are
            the same retrieval by construction rather than by agreement. */}
        <ExplanationPanel verdict={response.explanation} evidence={response.evidence} />
        <EvidencePanel evidence={response.evidence} results={response.results} />
      </div>
    </li>
  );
}

/**
 * The turn that is running.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY THIS NOW SAYS "Checking your records…", AND WHAT HAD TO BE TRUE FIRST.
 *
 * The previous version of this docblock explained why it deliberately did NOT:
 * those phrases are honest only on a surface that is TOLD which tool ran while
 * the turn runs, and no such channel existed. The turn route was a single
 * `reply.send` of a complete JSON response after the whole tool loop had
 * finished, so any per-step line here would have been a guess animated to look
 * like a report — the same fabrication the grounding rules forbid in prose,
 * unexempted by being a spinner. That docblock ended by naming its own
 * precondition: "when a channel exists that reports tool calls in flight, the
 * phrases become honest and belong here".
 *
 * F221b built the channel, so they belong here now. Every phrase below is set
 * from a server frame emitted when a tool round FINISHED — after the executor's
 * authorization gates ran, so it reports something that actually happened to a
 * caller who was actually allowed to do it. Nothing here infers a phase from
 * elapsed time or from the wording of the question. `phase === null` means the
 * turn has reported nothing yet, and the generic line is shown, which is still
 * the honest thing to say at that moment.
 *
 * WHAT IT STILL DOES NOT SHOW. Not the answer (the server sends prose once,
 * whole, after auditing and redacting it — nothing partial streams). Not tool
 * names, ids or counts: `phaseNote` maps a coarse server phase to a sentence
 * about the WORK, never about the machine, which keeps the runtime-vocabulary
 * firewall intact. And not a proposal — a change being drafted reads
 * "Preparing the change…" whether or not it will need a confirmation, because
 * saying which would let this screen know about a proposal before the server
 * has issued one.
 *
 * It disappears the moment the answer lands, and nothing replaces it: a
 * finished answer carries no narration of how it was produced. Progress DURING
 * a turn and a trace AFTER one are different things, and only the first exists.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE LIVE REGION, AND WHY THE ELAPSED COUNT LEFT IT.
 *
 * `role="status"` is an implicit `aria-live="polite"` region: a screen reader
 * announces its contents whenever they change. The elapsed seconds used to sit
 * INSIDE it, and they change once a second — so from second five onward this
 * surface asked a screen reader to interrupt itself every second to say a
 * number that carried no new information. Adding a phase line to the same node
 * would have compounded a defect rather than found one.
 *
 * So the region is split. The announced node holds the phase sentence ALONE.
 * It changes only when the KIND of work changes — a handful of times in a long
 * turn, each one a real event worth hearing — and two consecutive rounds in the
 * same phase render identical text, which React does not commit and a screen
 * reader therefore does not re-announce. The elapsed count is now a sibling
 * marked `aria-hidden`: still visible to anyone watching the clock, no longer
 * spoken to somebody who is trying to listen to something else.
 */
function WorkingTurn({
  inFlight,
  phase,
}: {
  inFlight: InFlight;
  phase: intelligence.TurnPhase | null;
}) {
  const seconds = useElapsedSeconds();
  // A CONFIRMED CHANGE KEEPS ITS OWN SENTENCE, REPORTED PHASE OR NOT.
  //
  // It is the one turn whose purpose the client already knows exactly, because
  // the person pressed Confirm on a summary of it a moment ago — and
  // "Applying the change you confirmed…" is both more specific than any phase
  // and stable for the whole turn. Letting a phase override it would trade a
  // precise sentence for a vaguer one ('preparing' is deliberately coarse
  // about whether a change was applied or merely proposed, because drawing
  // that line would leak a proposal) and would make the line flicker between
  // categories while a change the person authorized is being made. The
  // product decision that a confirm turn says it is APPLYING, not asking, is
  // pinned by `intelligence-workspace.test.tsx`; this keeps it true rather
  // than true-until-a-frame-arrives.
  const note =
    inFlight.kind === 'confirm'
      ? 'Applying the change you confirmed…'
      : phase !== null
        ? intelligence.phaseNote(phase)
        : 'Working on your question…';
  return (
    <div className="ap-turn ap-turn-working">
      {inFlight.kind === 'ask' ? (
        <div className="ap-said ap-said-you">
          <p className="ap-role">You</p>
          <p className="ap-text">{inFlight.question}</p>
        </div>
      ) : null}
      <div className="ap-said ap-said-phibian">
        <p className="ap-role">
          <BrandMark size={16} />
          Phibian
        </p>
        <p className="ap-working">
          <span className="ap-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {/* The ONLY announced node: one sentence, changing only with the
              phase. See the docblock on why the count is not in here. */}
          <span role="status">{note}</span>
          {seconds >= 5 ? (
            <span className="ap-elapsed" aria-hidden="true">
              {' '}
              {seconds}s
            </span>
          ) : null}
        </p>
      </div>
    </div>
  );
}

/** Whole seconds since this component mounted — measured here, so it is true. */
function useElapsedSeconds(): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const timer = setInterval(() => {
      setSeconds(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return seconds;
}

/**
 * Where the answer came from, in product language: "Based on Phibian records",
 * collapsed, expanding to lines a person can read.
 *
 * The summary line carries NO COUNT. "…— 2 retrievals" was a fact about the
 * tool loop wearing the costume of a confidence signal: two retrievals is not
 * better evidence than one, and a reader who starts comparing the numbers has
 * been taught to audit an implementation. What is worth knowing is what was
 * consulted, and that is one click away in words.
 *
 * The translation lives in `evidenceView.ts`, which is also where the two
 * firewalls are — no resource type and no tool id can reach these lines even
 * for a tool or a table this bundle has never heard of.
 */
function EvidencePanel({
  evidence,
  results,
}: {
  evidence: intelligence.TurnResponse['evidence'];
  results: intelligence.TurnResponse['results'];
}) {
  // NOTHING, WHEN THERE IS NOTHING (L182). This used to render "No records
  // were consulted for this answer." on every turn with an empty manifest —
  // which is every greeting, every clarification and, worst, every refusal.
  // The owner named that sentence: it reads as a verdict on the answer, and on
  // a refusal it reads as an accusation that Phibian did not even look.
  //
  // AND IT WAS ALREADY SAID, BETTER, TWELVE PIXELS ABOVE. An empty manifest
  // means no citation can resolve, so the grounding audit returns `uncited`
  // (or `ungrounded`), which is exactly the case `groundingCaution` renders as
  // "This answer isn't based on your organization's records — nothing in it
  // was checked against them." Same fact, aimed at the reader, in the one
  // place on the card that is for facts about how much to trust the answer.
  // This line was the same sentence again in the runtime's register, in the
  // provenance slot, where it looked like a fault report.
  //
  // So the provenance section says nothing when there is no provenance. It is
  // not a suppression: the caution above still fires, unchanged, every time.
  if (evidence.length === 0) return null;
  const lines = evidenceLines(evidence, results);
  return (
    <details className="ap-evidence">
      <summary className="rn-link ap-evidence-summary">Based on Phibian records</summary>
      {/* NUMBERED, and the number is load-bearing rather than decorative: it is
          the target of every citation marker the explanation above draws. An
          ordered list would number it too, and would number it the browser's
          way — this renders the server's own manifest position, which is the
          only ordering a marker can be resolved against. */}
      <ul className="ap-evidence-list" aria-label="What this answer is based on">
        {lines.map((line) => (
          <li key={line.key} className="rn-muted" data-citation={line.citation}>
            <span className="ap-evidence-cite" aria-hidden="true">
              [{line.citation}]
            </span>{' '}
            <span className="ap-sr-only">Record {line.citation}. </span>
            {line.text}
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * A proposed change, waiting on a human. Confirm re-posts the server-issued
 * pair; the server consumes its stored row atomically and only then lets the
 * change run. Dismiss is local — an unconsumed confirmation expires.
 *
 * The plan digest is NOT printed. It used to be, as "Plan 4f2a91c3…", on the
 * theory that naming the exact diff makes the confirmation precise. It does
 * not: a truncated hash is unreadable, unverifiable by the person reading it,
 * and indistinguishable from a reference number they are supposed to keep. The
 * digest still does its whole job — it rides the confirm request and the
 * server refuses any plan it does not match — and the sentence that was doing
 * the real work is the one that remains: this applies exactly the change
 * described above, and nothing else.
 */
function ConfirmationCard({
  pending,
  busy,
  onConfirm,
  onDismiss,
}: {
  pending: intelligence.ToolConfirmation;
  busy: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  return (
    <section className="rn-card ap-confirm" aria-label="Confirmation required">
      <h3 className="rn-card-title">Confirm this change before it runs</h3>
      <p className="ap-text">{pending.summary}</p>
      <p className="rn-muted">
        Confirming applies exactly the change described above, and nothing else.
      </p>
      <ConsequenceNote text="Confirming executes this change right away. Dismissing changes nothing — the proposal simply expires." />
      <div className="ap-confirm-actions">
        <button type="button" className="msg-primary" disabled={busy} onClick={onConfirm}>
          {busy ? 'Confirming…' : 'Confirm'}
        </button>
        <button type="button" className="rn-btn-quiet" disabled={busy} onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </section>
  );
}

/**
 * The question box, and the one control that stays put.
 *
 * It is docked rather than placed at the end of the thread: an assistant a
 * person has to scroll to talk to is one they stop talking to.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * ROOM FOR ATTACHMENT AND VOICE, WITHOUT SHIPPING EITHER (§32).
 *
 * The action row's left side is deliberately empty and is the slot those two
 * controls will occupy. They are not rendered — not greyed out, not behind a
 * "coming soon" tooltip — because the routes behind them are not mounted.
 * `@cwos/api-client` carries `intelligenceFiles` and `intelligenceVoice`, but
 * they are TYPES ONLY; the upload and transcription endpoints are lead-owned
 * and the intelligence plugin registers exactly two doors. A paperclip that
 * cannot accept a file is a defect a person discovers by trying, and a
 * disabled control is a promise with a date on it that nobody set. The layout
 * costs nothing until the routes land.
 *
 * ENTER SENDS; SHIFT+ENTER MAKES A NEWLINE. ⌘↵/Ctrl+↵ still sends, so the
 * habit the first slice taught still works.
 */
function Composer({
  id,
  textareaRef,
  draft,
  onDraftChange,
  onSubmit,
  pending,
  error,
  confirmationNotLive,
}: {
  id: string;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null> | undefined;
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: () => void;
  pending: boolean;
  error: unknown;
  confirmationNotLive: boolean;
}) {
  const canAsk = draft.trim() !== '' && !pending;
  // Grown from the CONTENT rather than from `scrollHeight`: jsdom has no
  // layout, so a measured autosize is a component that behaves one way in the
  // app and another way in every test that touches it. Counting newlines is
  // deterministic everywhere and is what a two-or-three-line question needs.
  const rows = Math.min(8, Math.max(1, draft.split('\n').length));
  return (
    <section className="rn-card msg-composer ap-composer">
      <label className="msg-label ap-sr-only" htmlFor={id}>
        Your question
      </label>
      <textarea
        id={id}
        ref={textareaRef}
        className="msg-textarea ap-textarea"
        value={draft}
        rows={rows}
        placeholder="Ask about your organization…"
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          if (event.shiftKey) return;
          event.preventDefault();
          onSubmit();
        }}
      />
      {error !== null ? (
        <p className="msg-error" role="alert">
          {confirmationNotLive
            ? 'That confirmation was no longer live — it may have expired, already been used, or the plan changed. Nothing ran. Ask again to get a fresh proposal.'
            : 'We couldn’t get an answer just now. Your question is still here — try again.'}
        </p>
      ) : null}
      <div className="msg-composer-actions ap-composer-actions">
        {/* Attachment and voice land here when their routes do. */}
        <span className="ap-composer-hint rn-muted">
          Enter to send · Shift+Enter for a new line
        </span>
        <button
          type="button"
          className="msg-primary ap-send"
          disabled={!canAsk}
          aria-label="Ask"
          onClick={onSubmit}
        >
          {pending ? 'Asking…' : <Icon name="arrowRight" size={16} />}
        </button>
      </div>
    </section>
  );
}

/**
 * The honest not-configured state (routes.ts answers 503
 * `intelligence_provider_unconfigured` before any state changes). No retry
 * button: asking again cannot configure a provider.
 */
function NotConfigured() {
  return (
    <section className="rn-card ap-notconfigured" role="status">
      <h3 className="rn-card-title">Ask Phibian isn’t configured for this deployment yet</h3>
      <p className="rn-muted">
        The assistant needs a language-model provider, and this deployment doesn’t have one set up.
        Nothing failed and nothing was lost — there is just no provider to ask. Whoever operates
        this deployment can enable it.
      </p>
    </section>
  );
}
